import type { ReaderTag } from '@/api/client';

/**
 * Фиксированный порядок групп из миграции 110. Синхронизировать с seed SQL.
 */
export const READER_TAG_CANONICAL_GROUP_ORDER: readonly string[] = [
  'Статус',
  'Пол и тип голоса',
  'Тембр и высота',
  'Манера чтения / тон',
  'Жанровая специализация',
  'Ролевые навыки',
  'Контентные ограничения',
  'Технические / студийные теги',
  'Надёжность и рабочий процесс',
  'Уровень',
] as const;

/** Теги вне справочника / без группы */
export const READER_TAG_FALLBACK_GROUP = 'Прочее';

/**
 * Порядок групп тегов фонотеки (миграция 121). Синхронизировать с seed SQL.
 */
export const MUSIC_TAG_CANONICAL_GROUP_ORDER: readonly string[] = [
  'Статус / права',
  'Назначение в проекте',
  'Настроение / эмоция',
  'Жанр / стиль',
  'Темп / динамика',
  'Инструменты / фактура',
  'Сцена / контекст использования',
  'Технические теги',
] as const;

/**
 * Палитра тегов (#RRGGBB lowercase). Должна совпадать с server/src/constants/readerTagPalette.ts
 */
export const READER_TAG_PALETTE_HEX: readonly string[] = [
  '#f54242',
  '#000000',
  '#ffb957',
  '#ff7b00',
  '#43c021',
  '#42a5f5',
  '#48b9ea',
  '#8280f5',
  '#504dd5',
  '#af86d0',
  '#f59999',
  '#e561e1',
  '#858585',
  '#00897b',
] as const;

/** Краткие подсказки для выбора цвета (блок «итоговая логика» в ТЗ) */
export const READER_TAG_PALETTE_HINTS: Readonly<Record<string, string>> = {
  '#f54242': 'Запреты, проблемы, риски',
  '#000000': 'Жёсткое «нет», сильное ограничение',
  '#ffb957': 'Внимание, документы, контроль',
  '#ff7b00': 'Яркий, нестандартный, комедия',
  '#43c021': 'Хорошо, положительное качество',
  '#42a5f5': 'Профессиональный, стабильный',
  '#48b9ea': 'Нонфикшн, знания',
  '#8280f5': 'Спокойный, глубокий',
  '#504dd5': 'Топ, сложное',
  '#af86d0': 'Художественное, драма',
  '#f59999': 'Тёплый, мягкий',
  '#e561e1': 'Детское, эмоциональное',
  '#858585': 'Нейтральное',
  '#00897b': 'Разговорный, дружелюбный формат',
};

export function sortReaderTagsInGroup(tags: ReaderTag[]): ReaderTag[] {
  return [...tags].sort(
    (a, b) =>
      a.display_order - b.display_order ||
      a.name.localeCompare(b.name, 'ru') ||
      a.id.localeCompare(b.id)
  );
}

function sortGroupKeys(
  keys: string[],
  canonicalOrder: readonly string[],
  canonicalSet: Set<string>,
  fallbackGroup: string
): string[] {
  const keySet = new Set(keys);
  const canonical = canonicalOrder.filter((g) => keySet.has(g));
  const custom = keys
    .filter((k) => !canonicalSet.has(k) && k !== fallbackGroup)
    .sort((a, b) => a.localeCompare(b, 'ru'));
  const tail = keySet.has(fallbackGroup) ? [fallbackGroup] : [];
  return [...canonical, ...custom, ...tail];
}

export function groupCatalogTagsByGroup<T extends { tag_group: string; display_order: number; name: string; id: string }>(
  catalog: T[],
  canonicalGroupOrder: readonly string[],
  fallbackGroup: string
): { group: string; tags: T[] }[] {
  const canonicalSet = new Set<string>(canonicalGroupOrder);
  const byGroup = new Map<string, T[]>();
  for (const t of catalog) {
    const g = (t.tag_group ?? '').trim() || fallbackGroup;
    const prev = byGroup.get(g);
    if (prev) prev.push(t);
    else byGroup.set(g, [t]);
  }
  for (const arr of byGroup.values()) {
    arr.sort(
      (a, b) =>
        a.display_order - b.display_order ||
        a.name.localeCompare(b.name, 'ru') ||
        a.id.localeCompare(b.id)
    );
  }
  return sortGroupKeys(Array.from(byGroup.keys()), canonicalGroupOrder, canonicalSet, fallbackGroup).map((group) => ({
    group,
    tags: byGroup.get(group) ?? [],
  }));
}

export function groupReaderTagsByGroup(catalog: ReaderTag[]): { group: string; tags: ReaderTag[] }[] {
  return groupCatalogTagsByGroup(catalog, READER_TAG_CANONICAL_GROUP_ORDER, READER_TAG_FALLBACK_GROUP);
}

export function groupMusicTagsByGroup(catalog: ReaderTag[]): { group: string; tags: ReaderTag[] }[] {
  return groupCatalogTagsByGroup(catalog, MUSIC_TAG_CANONICAL_GROUP_ORDER, READER_TAG_FALLBACK_GROUP);
}
